import React, { useState, useEffect } from 'react';
import Button from './ui/Button';
import Input from './ui/Input';
import {characterClasses} from "../utils/characterTranslation";

const EditCharacterModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  character, 
  loading = false,
  isAdmin = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    class: 'GUERREIRA',
    level: 1,
    experience: 0,
    status: 'ACTIVE'
  });

  const [errors, setErrors] = useState({});

  const statusOptions = [
    { value: 'ACTIVE', label: 'Ativo' },
    { value: 'INACTIVE', label: 'Inativo' },
  ];

  // Update form data when character prop changes
  useEffect(() => {
    if (character) {
      setFormData({
        name: character.name || '',
        class: character.class || 'WARRIOR',
        level: character.level || 1,
        experience: character.experience || 0,
        status: character.status || 'ACTIVE'
      });
    }
  }, [character]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'level' || name === 'experience' ? parseInt(value) || 0 : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const level = formData.level ? formData.level : character.level
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Nome deve ter pelo menos 2 caracteres';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Nome deve ter no máximo 50 caracteres';
    }

    if (level < 1 || level > 180) {
      newErrors.level = 'Level deve estar entre 1 e 180';
    }

    if (formData.experience < 0) {
      newErrors.experience = 'Experiência não pode ser negativa';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const resetForm = () => {
    if (character) {
      setFormData({
        name: character.name || '',
        class: character.class || 'WARRIOR',
        level: character.level || 1,
        experience: character.experience || 0,
        status: character.status || 'ACTIVE'
      });
    }
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-white/10 shadow-2xl">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">Editar Personagem</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white hover:bg-white/10 rounded-full w-8 h-8 flex items-center justify-center transition-colors text-2xl"
              disabled={loading}
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                Nome do Personagem
              </label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Digite o nome do personagem"
                disabled={loading}
              />
              {errors.name && (
                <p className="text-red-400 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label htmlFor="class" className="block text-sm font-medium text-gray-300 mb-1">
                Classe
              </label>
              <select
                id="class"
                name="class"
                value={formData.class}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {characterClasses.map(({ value, label, emoji }) => (
                  <option key={value} value={value} className="bg-slate-800 text-white">
                    {emoji} {label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-300 mb-1">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {statusOptions.map(({ value, label }) => (
                  <option key={value} value={value} className="bg-slate-800 text-white">
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {isAdmin && (
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label htmlFor="level" className="block text-sm font-medium text-gray-300 mb-1">
                    Level
                  </label>
                  <Input
                    id="level"
                    name="level"
                    type="number"
                    min="1"
                    max="180"
                    value={formData.level}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.level && (
                    <p className="text-red-400 text-sm mt-1">{errors.level}</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t border-white/10 mt-6">
              <div className="flex-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={loading}
                  fullWidth
                >
                  Cancelar
                </Button>
              </div>
              <div className="flex-1">
                <Button
                  type="submit"
                  variant="primary"
                  loading={loading}
                  fullWidth
                >
                  {loading ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditCharacterModal;